import { SharedService } from '../../shared/shared.service';
import { from, mergeMap, Observable, throwError } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { catchError, finalize } from 'rxjs/operators';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import { RoutesEnum } from '../utils/RoutesEnum';
import { NzMessageService } from 'ng-zorro-antd/message';
import { AuthService } from './auth.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';

export const InterceptorSkipHeader = 'X-Skip-Interceptor-Header';
export const InterceptorSkipLoader = 'X-Skip-Interceptor-Loader';

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptorService implements HttpInterceptor {
  private sharedService = inject(SharedService);
  private Router = inject(Router);
  private authService = inject(AuthService);
  private NzMessageService = inject(NzMessageService);
  private oidcSecurityService = inject(OidcSecurityService);

  totalRequests = 0;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<any>> {
    const skipLoader = req.headers.has(InterceptorSkipLoader);
    if (skipLoader) {
      req = req.clone({ headers: req.headers.delete(InterceptorSkipLoader) });
      this.hideLoader();
    } else {
      this.showLoader();
      this.totalRequests++;
    }

    const isOidcRequest = req.url.includes('/userInfo') || req.url.includes('/token') || req.url.includes('/oauth2');

    const token$ = isOidcRequest
      ? from(this.oidcSecurityService.getAccessToken())
      : from(this.oidcSecurityService.getIdToken());

    return token$.pipe(
      mergeMap((token) => {
        const modifiedReq = token
          ? req.clone({
              headers: req.headers.set('Authorization', `Bearer ${token}`),
            })
          : req;

        return next.handle(modifiedReq).pipe(
          catchError((err) => this.handleError(err)),
          finalize(() => this.decrementLoader()),
        );
      }),
    );
  }

  private handleError(err: any): Observable<never> {
    switch (err.status) {
      case 400:
        if (err?.error?.status?.message.length) {
          this.NzMessageService.error(err.error.status.message);
        } else {
          this.NzMessageService.error('Bad request.');
        }
        break;
      case 401:
        this.NzMessageService.error('Unauthorized for login issues.');
        this.authService.logout();
        break;
      case 403:
        this.NzMessageService.error('Forbidden for AccessDeniedException');
        break;
      case 404:
        this.NzMessageService.error('Not found.');
        this.Router.navigateByUrl(RoutesEnum.PageNotFound);
        break;
      case 409:
        this.NzMessageService.error('Conflict for tenant exceptions and duplicates into DB.');
        break;
      case 500:
        this.NzMessageService.error('Unexpected issue happened, please try again.');
        break;
      default:
        this.NzMessageService.error(
          'Oops! Something went wrong on our end. Please try again later. ' +
            'If this issue persists, contact the administrator.',
        );
    }
    return throwError(() => err);
  }

  private decrementLoader(): void {
    this.totalRequests--;
    if (this.totalRequests === 0) {
      this.hideLoader();
    }
  }

  private showLoader(): void {
    this.sharedService.showLoader();
  }

  private hideLoader(): void {
    this.sharedService.hideLoader();
  }
}
